import {
  ITableColumn,
  ITableDataItem,
  ITableFilter,
  formatDate,
  deleteProps,
} from "@inceptionbg/ui-components";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../AppContent";
import { Table } from "../Components/CustomIUI/Table/Table";
import { getNotifications } from "../repos/NotificationRepo";
import {
  INotification,
  INotificationFilters,
  NotificationTypes,
} from "../types/INotification";
import { NotificationDetailsDialog } from "../Components/Dialogs/NotificationDetailsDialog";
import { DateInput } from "@inceptionbg/ui-components";
import { Select } from "../Components/Inputs/Select";
import { Loader } from "../Elements/Loader";

export const NotificationsPage: FC = () => {
  const [data, setData] = useState<ITableDataItem[]>([]);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<INotification | null>(null);
  const [filters, setFilters] = useState<INotificationFilters>({});
  const [filterData, setFilterData] = useState<INotificationFilters>({});

  const { t } = useTranslation();
  const farmUuid = useContext(FarmContext).farm?.uuid;

  const columns: ITableColumn[] = [
    { id: "type", label: t("NotificationType") },
    { id: "channel", label: t("NotificationChannel") },
    { id: "message", label: t("NotificationMessage") },
    { id: "createdAt", label: t("NotificationSentDate") },
  ];

  const handleGetNotifications = useCallback(() => {
    if (farmUuid) {
      setIsLoading(true);
      getNotifications(farmUuid, { limit, offset }, filterData)
        .then(({ notifications, totalRows }) => {
          setData(
            notifications.map((notification) => ({
              uuid: notification.uuid,
              onRowClick: () => setSelectedNotification(notification),
              cells: {
                type: { value: t(`NotificationType${notification.type}`) },
                channel: {
                  value: t(
                    `CommunicationChannel${notification.communicationChannelType}`
                  ),
                },
                message: { value: notification.message },
                createdAt: { value: formatDate(notification.createdAt) },
              },
            })) as ITableDataItem[]
          );
          setTotalRows(totalRows);
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [farmUuid, limit, offset, filterData, t]);

  useEffect(handleGetNotifications, [handleGetNotifications]);

  const onSearch = (search: INotificationFilters) => {
    setFilterData(search);
    setOffset(0);
  };

  const notificationFilters: (
    search: INotificationFilters,
    setSearch: (search: INotificationFilters) => void
  ) => ITableFilter = (search, setSearch) => ({
    type: {
      label: t("NotificationType"),
      field: (
        <Select
          value={
            search.type
              ? {
                  label: t(`NotificationType${search.type}`),
                  value: search.type,
                }
              : null
          }
          options={NotificationTypes.map((e) => ({
            label: t(`NotificationType${e}`),
            value: e,
          }))}
          onChange={(e) => setSearch({ ...search, type: e?.value })}
          isClearable
          placeholder={t("ChoosePlaceholder")}
        />
      ),
      resetField: () => setSearch(deleteProps(search, ["type"])),
    },
    date: {
      label: `${t("NotificationSentDate")} ${t("from")}`,
      field: (
        <div className="flex-center full-width">
          <DateInput
            date={search.dateBefore || ""}
            setDate={(dateBefore) => setSearch({ ...search, dateBefore })}
          />
          <p className="mx-2">{t("to")}</p>
          <DateInput
            date={search.dateAfter || ""}
            setDate={(dateAfter) => setSearch({ ...search, dateAfter })}
          />
        </div>
      ),
      resetField: () =>
        setSearch(deleteProps(search, ["dateAfter", "dateBefore"])),
    },
  });

  return (
    <>
      <Loader isLoading={isLoading}>
        <Table
          columns={columns}
          data={data}
          serverSidePagination={{
            limit,
            offset,
            setLimit,
            setOffset,
            totalRows,
          }}
          filterData={{
            columns: ["type", "date"],
            filters: notificationFilters(filters, setFilters),
            search: filterData,
            searchData: filters,
            setSearchData: setFilters,
            onSubmit: onSearch,
          }}
          footerAction={{
            onClick: handleGetNotifications,
          }}
        />
      </Loader>

      {selectedNotification && (
        <NotificationDetailsDialog
          isOpen={!!selectedNotification}
          onClose={() => setSelectedNotification(null)}
          notification={selectedNotification}
        />
      )}
    </>
  );
};
